// filepath: src/components/ResetPassword.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  // Query params
  const params = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const mode = params.get("mode") || "";
  const oobCode = params.get("oobCode") || "";

  // If we have a valid oobCode + mode, we’re in CONFIRM mode
  const isConfirmStage = mode === "resetPassword" && !!oobCode;

  // Request stage state
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  // Confirm stage state
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(isConfirmStage);
  const [codeEmail, setCodeEmail] = useState(""); // email retrieved from code

  // Common UX
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // If in confirm stage, verify the oobCode to fetch the target email (useful for UX)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isConfirmStage) return;
      setErr(""); setOk(""); setVerifyingCode(true);
      try {
        const targetEmail = await verifyPasswordResetCode(auth, oobCode);
        if (!alive) return;
        setCodeEmail(targetEmail || "");
      } catch (e) {
        if (!alive) return;
        setErr("This reset link is invalid or has expired. Please request a new one.");
      } finally {
        if (alive) setVerifyingCode(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [isConfirmStage, oobCode]);

  // Request stage: send reset email via your backend function
  async function sendResetLink(e) {
    e.preventDefault();
    setErr(""); setOk(""); setSending(true);
    try {
      const res = await fetch("/api/auth/sendReset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `Failed (${res.status})`);
      }
      setOk("If an account exists for that email, a reset link has been sent.");
      setEmail("");
    } catch (e) {
      // Be deliberately vague to avoid user enumeration
      setOk("If an account exists for that email, a reset link has been sent.");
    } finally {
      setSending(false);
    }
  }

  // Confirm stage: actually change the password using the oobCode
  async function completeReset(e) {
    e.preventDefault();
    setErr(""); setOk("");
    if (newPass.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (newPass !== newPass2) {
      setErr("Passwords do not match.");
      return;
    }
    try {
      await confirmPasswordReset(auth, oobCode, newPass);
      setOk("Your password has been updated. You can now sign in.");
      // Small delay then go to login
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (e) {
      setErr("We couldn’t update your password. The link may be invalid or expired.");
    }
  }

  return (
    <div className="rp">
      <div className="rp__card">
        <div className="rp__brand">
          <img src="/Lastwishbox-logo.png" alt="LastWish Box" className="rp__logo" />
          <h2 className="rp__title">{isConfirmStage ? "Choose a new password" : "Reset your password"}</h2>
        </div>

        {!isConfirmStage && (
          <form onSubmit={sendResetLink} className="rp__form">
            <p className="rp__text">
              Enter your email and we’ll send you a secure link to reset your password.
            </p>
            <label className="rp__label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rp__input"
              placeholder="you@example.com"
              required
            />
            <button className="rp__btn rp__btn--main" disabled={sending}>
              {sending ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        {isConfirmStage && (
          <form onSubmit={completeReset} className="rp__form">
            <p className="rp__text">
              {verifyingCode
                ? "Checking your reset link…"
                : codeEmail
                  ? <>Resetting password for <b>{codeEmail}</b></>
                  : "Enter a new password below."}
            </p>
            <label className="rp__label">New password</label>
            <input
              type="password"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              className="rp__input"
              required
            />
            <label className="rp__label">Confirm new password</label>
            <input
              type="password"
              value={newPass2}
              onChange={(e) => setNewPass2(e.target.value)}
              className="rp__input"
              required
            />
            <button className="rp__btn rp__btn--main" disabled={verifyingCode}>
              Update password
            </button>
          </form>
        )}

        {(err || ok) && (
          <div className="rp__messages">
            {err && <div className="rp__error">{err}</div>}
            {ok && <div className="rp__ok">{ok}</div>}
          </div>
        )}

        <div className="rp__links">
          <button type="button" className="rp__link" onClick={() => navigate("/login")}>
            Back to sign in
          </button>
        </div>
      </div>

      <style>{`
        .rp{min-height:100vh;display:grid;place-items:center;background:#fafafa;padding:20px}
        .rp__card{width:100%;max-width:480px;background:#fff;border-radius:14px;box-shadow:0 10px 40px #2a05161a;padding:24px}
        .rp__brand{display:flex;align-items:center;gap:12px;margin-bottom:12px}
        .rp__logo{height:40px;width:auto}
        .rp__title{margin:0;color:#2a0516}
        .rp__form{display:flex;flex-direction:column;gap:8px}
        .rp__label{font-size:12px;color:#6b7280;font-weight:700}
        .rp__input{width:100%;min-height:44px;padding:10px 12px;border:1px solid #d6d6e7;border-radius:10px;background:#fff}
        .rp__input:focus{outline:none;border-color:#8fa0ff;box-shadow:0 0 0 3px rgba(79,70,229,.12)}
        .rp__btn{display:block;width:100%;height:44px;border-radius:10px;border:1px solid #d1d5db;background:#fff;font-weight:800;cursor:pointer}
        .rp__btn--main{background:#f15822;border-color:#f15822;color:#fff;margin-top:4px}
        .rp__messages{margin-top:10px}
        .rp__error{color:#a00}
        .rp__ok{color:#065f46}
        .rp__links{margin-top:10px}
        .rp__link{background:none;border:none;color:#003353;font-weight:800;cursor:pointer;text-decoration:underline;padding:0}
        .rp__text{color:#374151;margin:4px 0 10px}
      `}</style>
    </div>
  );
}
